import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';
import formatCurrency from '@afosto/utils/formatCurrency';
import getFullName from '@afosto/utils/getFullName';
import { contacts as contactsApi } from '@afosto/contacts/api/Contacts';
import { organisations as organisationsApi } from '@afosto/contacts/api/Organisations';
import { datetime } from '@afosto/app-components-react/DataGrid/formatters';
import translations from './translations';

export const InvoiceCardSkeleton = () => (
  <Grid item xs={12}>
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" style={{ width: '40%' }}>
            <Skeleton variant="text" />
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ width: '30%' }}>
            <Skeleton variant="text" />
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Box width="50%">
            <Typography variant="body2" color="textSecondary" style={{ width: '50%' }}>
              <Skeleton variant="text" />
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ width: '35%' }}>
              <Skeleton variant="text" />
            </Typography>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="baseline" mt={2}>
          <Typography variant="body2" color="textSecondary" style={{ width: '25%' }}>
            <Skeleton variant="text" />
          </Typography>
          <Typography variant="subtitle1" style={{ width: '40%' }}>
            <Skeleton variant="text" />
          </Typography>
        </Box>
      </CardContent>
    </Card>
  </Grid>
);

const InvoiceCard = ({ invoice }) => {
  const intl = useIntl();
  const history = useHistory();
  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(true);
  const [organisation, setOrganisation] = useState(null);
  const [isLoadingOrganisation, setIsLoadingOrganisation] = useState(true);
  const { contact_id: contactID, organisation_id: organisationID } = invoice;

  useEffect(() => {
    let isMounted = true;

    const fetchContact = async id => {
      setIsLoadingContact(true);

      try {
        const contactResponse = await contactsApi.getContact(id);
        const { data } = contactResponse;

        if (isMounted) {
          setContact(data);
        }
      } catch (error) {
        if (isMounted) {
          setContact(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingContact(false);
        }
      }
    };

    const fetchOrganisation = async id => {
      setIsLoadingOrganisation(true);

      try {
        const organisationResponse = await organisationsApi.viewOrganisation(id);
        const { data } = organisationResponse;

        if (isMounted) {
          setOrganisation(data);
        }
      } catch (error) {
        if (isMounted) {
          setOrganisation(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingOrganisation(false);
        }
      }
    };

    if (contactID) {
      fetchContact(contactID);
    } else {
      setIsLoadingContact(false);
    }

    if (organisationID) {
      fetchOrganisation(organisationID);
    } else {
      setIsLoadingOrganisation(false);
    }

    return () => {
      isMounted = false;
    };
  }, [contactID, organisationID]);

  return (
    <Grid item xs={12}>
      <Card>
        <CardActionArea onClick={() => history.push(`/invoices/${invoice?.id}`)}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">{invoice?.number}</Typography>
              <Typography variant="caption" color="textSecondary">
                {datetime(invoice?.created_at)}
              </Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <div style={{ minWidth: '50%' }}>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: '50%' }}>
                  {isLoadingContact ? (
                    <Skeleton variant="text" width="50%" />
                  ) : (
                    getFullName(contact ?? {})
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ minWidth: '35%' }}>
                  {isLoadingOrganisation ? (
                    <Skeleton variant="text" width="35%" />
                  ) : (
                    organisation?.name
                  )}
                </Typography>
              </div>
              <Chip
                size="small"
                variant="outlined"
                color={invoice?.is_paid ? 'default' : 'secondary'}
                label={
                  invoice?.is_paid
                    ? intl.formatMessage(translations.invoiceCard.status.paid)
                    : intl.formatMessage(translations.invoiceCard.status.unpaid)
                }
              />
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="baseline" mt={2}>
              <Typography variant="body2" color="textSecondary">
                {invoice?.is_including_vat
                  ? intl.formatMessage(translations.invoiceCard.includingVat)
                  : intl.formatMessage(translations.invoiceCard.excludingVat)}
              </Typography>
              <Typography variant="subtitle1">{formatCurrency(invoice?.total / 100)}</Typography>
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );
};

InvoiceCard.propTypes = {
  invoice: PropTypes.object,
};

InvoiceCard.defaultProps = {
  invoice: {},
};

export default InvoiceCard;
